/**
 * Service worker registration and unregistration utilities.
 *
 * Provides functions to register a minimal service worker that satisfies
 * browser PWA installability requirements and to unregister all active
 * service workers when needed.
 *
 * @module pwa/service-worker
 */

/** Default URL path for the service worker script. */
const DEFAULT_SW_URL = '/service-worker.js';

/**
 * Registers a service worker at the given URL.
 *
 * Checks for browser support before attempting registration. If the browser
 * does not support service workers or if registration fails, the function
 * logs a warning and returns `null` instead of throwing.
 *
 * @param swUrl - URL of the service worker script. Defaults to `'/service-worker.js'`.
 * @returns A promise that resolves to the {@link ServiceWorkerRegistration} on success,
 *          or `null` if service workers are not supported or registration fails.
 *
 * @example
 * ```ts
 * const registration = await registerServiceWorker();
 * if (registration) {
 *   console.log('Service worker registered:', registration.scope);
 * }
 * ```
 */
export async function registerServiceWorker(
  swUrl: string = DEFAULT_SW_URL,
): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn(
      'Service workers are not supported in this browser. PWA installability may be limited.',
    );
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register(swUrl);
    return registration;
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    console.warn(`Service worker registration failed: ${message}`);
    return null;
  }
}

/**
 * Unregisters all active service workers.
 *
 * Iterates over every service worker registration in the current scope
 * and calls `unregister()` on each. Logs a warning if any individual
 * unregistration fails but does not throw.
 *
 * @returns A promise that resolves when all unregistration attempts have completed.
 *
 * @example
 * ```ts
 * await unregisterServiceWorker();
 * ```
 */
export async function unregisterServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();

  await Promise.all(
    registrations.map(async (registration) => {
      try {
        await registration.unregister();
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        console.warn(`Failed to unregister service worker: ${message}`);
      }
    }),
  );
}
